import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import Layout from './components/layout/Layout';
import LoginPage from './pages/LoginPage';
import AccessDenied from './pages/AccessDenied';
import Dashboard from './components/dashboard/Dashboard';
import Forecasts from './pages/Forecasts';
import MRPWizard from './pages/MRPWizard';
import OrderRecommendation from './pages/OrderRecommendation';
import ItemMaster from './pages/ItemMaster';
import ItemMasterDetail from './pages/ItemMasterDetail';
import PurchaseOrders from './pages/PurchaseOrders';
import PurchaseOrderDetail from './pages/PurchaseOrderDetail';
import GoodsReceiptPo from './pages/GoodsReceiptPo';
import ProductionOrders from './pages/ProductionOrders';
import ProductionOrderDetail from './pages/ProductionOrderDetail';
import Vendors from './pages/Vendors';
import CalendarSettings from './pages/CalendarSettings';
import CompanyCalendar from './pages/CompanyCalendar';
import UserGuide from './pages/UserGuide';
import AdminPanel from './pages/AdminPanel';
import SalesOrders from './pages/SalesOrders';
import SalesOrderDetail from './pages/SalesOrderDetail';
import BillOfMaterials from './pages/BillOfMaterials';
import BomCsvImport from './pages/BomCsvImport';
import ResourceMaster from './pages/ResourceMaster';
import ResourceDetail from './pages/ResourceDetail';
import ReworkOrders from './pages/ReworkOrders';
import ReworkOrderDetail from './pages/ReworkOrderDetail';
import VendorDetail from './pages/VendorDetail';
import InventoryPostingList from './pages/InventoryPostingList';
import InventoryTransfers from './pages/InventoryTransfers';
import InventoryTransferDetail from './pages/InventoryTransferDetail';
import InventoryTransferRequests from './pages/InventoryTransferRequests';
import InventoryTransferRequestDetail from './pages/InventoryTransferRequestDetail';
import WarehouseSetup from './pages/WarehouseSetup';
import Notifications from './pages/Notifications';
import EmailSettings from './pages/EmailSettings';
import SapB1Settings from './pages/SapB1Settings';
import SapWebhookMonitor from './pages/SapWebhookMonitor';
import SapUpdateCheck from './pages/SapUpdateCheck';
import NumberingSettings from './pages/NumberingSettings';
import IssueForProduction from './pages/IssueForProduction';
import IssueForProductionDetail from './pages/IssueForProductionDetail';
import ReceiptFromProduction from './pages/ReceiptFromProduction';
import ReceiptFromProductionDetail from './pages/ReceiptFromProductionDetail';
import CustomerReceivablesAging from './pages/CustomerReceivablesAging';
import VendorLiabilitiesAging from './pages/VendorLiabilitiesAging';
import VendorManagement from './pages/VendorManagement';
import VendorPerformance from './pages/VendorPerformance';
import VendorScore from './pages/VendorScore';
import VendorRiskManagement from './pages/VendorRiskManagement';
import VendorMgmtDetail from './pages/VendorMgmtDetail';
import CustomerBusinessPartnerOverview from './pages/CustomerBusinessPartnerOverview';
import InventoryAgingReport from './pages/InventoryAgingReport';
import InventoryTransferReport from './pages/InventoryTransferReport';
import NewProcurementItemsReport from './pages/NewProcurementItemsReport';
import TotalProjects from './pages/TotalProjects';
import VendorRiskDetail from './pages/VendorRiskDetail';
import VendorRiskList from './pages/VendorRiskList';
import VendorDeliveryPerformance
from './pages/VendorDeliveryPerformance';
import DeliveryVendorList from './pages/DeliveryVendorList';
import QualityVendorList
from "./pages/QualityVendorList";
import ProcurementVendorList from './pages/ProcurementVendorList';
import FinancialVendorList
from "./pages/FinancialVendorList";
import VendorActivity from './pages/vendorActivity';


/* ── Protected route wrapper ── */
function ProtectedRoute({ children, moduleKey, anyModule }) {
  const { isAuthenticated, hasModuleAccess } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (anyModule && anyModule.length) {
    if (!anyModule.some((k) => hasModuleAccess(k))) return <AccessDenied />;
    return children;
  }
  if (moduleKey && !hasModuleAccess(moduleKey)) return <AccessDenied />;
  return children;
}

/* ── Admin-only route ── */
function AdminRoute({ children }) {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user?.role_name !== 'Super Admin') return <AccessDenied />;
  return children;
}

/* ── Admin & Super Admin route ── */
function AdminOrSuperAdminRoute({ children }) {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!['Super Admin', 'Admin'].includes(user?.role_name)) return <AccessDenied />;
  return children;
}

export default function App() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-sap-bg dark:bg-slate-950">
        <div className="text-center">
  <img
    src="/logo.jpg"
    alt="Agentfloww Logo"
    className="h-16 w-auto mx-auto mb-4"
  />

  <div className="animate-spin h-6 w-6 border-2 border-sap-blue border-t-transparent rounded-full mx-auto"></div>
</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <>
      <Layout>
        <Routes>
          <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/mrp/forecasts" element={<ProtectedRoute moduleKey="forecasts"><Forecasts /></ProtectedRoute>} />
          <Route path="/mrp/wizard" element={<ProtectedRoute moduleKey="mrp"><MRPWizard /></ProtectedRoute>} />
          <Route path="/mrp/recommendations" element={<ProtectedRoute moduleKey="mrp"><OrderRecommendation /></ProtectedRoute>} />
          <Route path="/mrp/calendar" element={<ProtectedRoute moduleKey="calendar"><CompanyCalendar /></ProtectedRoute>} />
          <Route path="/inventory/items" element={<ProtectedRoute moduleKey="inventory"><ItemMaster /></ProtectedRoute>} />
          <Route path="/inventory/postings" element={<ProtectedRoute moduleKey="inventory"><InventoryPostingList /></ProtectedRoute>} />
          <Route path="/inventory/transfer-requests" element={<ProtectedRoute moduleKey="inventory"><InventoryTransferRequests /></ProtectedRoute>} />
          <Route path="/inventory/transfer-request/:id" element={<ProtectedRoute moduleKey="inventory"><InventoryTransferRequestDetail /></ProtectedRoute>} />
          <Route path="/inventory/transfers" element={<ProtectedRoute moduleKey="inventory"><InventoryTransfers /></ProtectedRoute>} />
          <Route path="/inventory/aging" element={<ProtectedRoute moduleKey="inventory"><InventoryAgingReport /></ProtectedRoute>} />
          <Route path="/inventory/transfer-report" element={<ProtectedRoute moduleKey="inventory"><InventoryTransferReport /></ProtectedRoute>} />
          <Route path="/inventory/new-procurement-report" element={<ProtectedRoute moduleKey="inventory"><NewProcurementItemsReport /></ProtectedRoute>} />
          <Route path="/inventory/transfer/:id" element={<ProtectedRoute moduleKey="inventory"><InventoryTransferDetail /></ProtectedRoute>} />
          <Route path="/admin/setup/warehouses" element={<ProtectedRoute moduleKey="inventory"><WarehouseSetup /></ProtectedRoute>} />
          <Route path="/item-master/:itemNo" element={<ProtectedRoute moduleKey="inventory"><ItemMasterDetail /></ProtectedRoute>} />
          <Route path="/sales/orders" element={<ProtectedRoute moduleKey="sales"><SalesOrders /></ProtectedRoute>} />
          <Route path="/sales-order/:id" element={<ProtectedRoute moduleKey="sales"><SalesOrderDetail /></ProtectedRoute>} />
          <Route path="/purchasing/orders" element={<ProtectedRoute moduleKey="purchasing"><PurchaseOrders /></ProtectedRoute>} />
          <Route path="/purchasing/vendors" element={<ProtectedRoute moduleKey="purchasing"><Vendors /></ProtectedRoute>} />
          <Route path="/vendor/:id" element={<ProtectedRoute anyModule={['purchasing', 'financials']}><VendorDetail /></ProtectedRoute>} />
          <Route path="/purchase-order/:id" element={<ProtectedRoute moduleKey="purchasing"><PurchaseOrderDetail /></ProtectedRoute>} />
          <Route path="/purchasing/goods-receipt-po" element={<ProtectedRoute moduleKey="purchasing"><GoodsReceiptPo /></ProtectedRoute>} />
          <Route path="/production/bom" element={<ProtectedRoute moduleKey="production"><BillOfMaterials /></ProtectedRoute>} />
          <Route path="/production/bom-import" element={<ProtectedRoute moduleKey="production"><BomCsvImport /></ProtectedRoute>} />
          <Route path="/production/orders" element={<ProtectedRoute moduleKey="production"><ProductionOrders /></ProtectedRoute>} />
          <Route path="/production/issues" element={<ProtectedRoute moduleKey="production"><IssueForProduction /></ProtectedRoute>} />
          <Route path="/production/issue/:id" element={<ProtectedRoute moduleKey="production"><IssueForProductionDetail /></ProtectedRoute>} />
          <Route path="/production/receipts" element={<ProtectedRoute moduleKey="production"><ReceiptFromProduction /></ProtectedRoute>} />
          <Route path="/production/receipt/:id" element={<ProtectedRoute moduleKey="production"><ReceiptFromProductionDetail /></ProtectedRoute>} />
          <Route path="/production-order/:id" element={<ProtectedRoute moduleKey="production"><ProductionOrderDetail /></ProtectedRoute>} />
          <Route path="/production/rework" element={<ProtectedRoute moduleKey="production"><ReworkOrders /></ProtectedRoute>} />
          <Route path="/rework/:id" element={<ProtectedRoute moduleKey="production"><ReworkOrderDetail /></ProtectedRoute>} />
          <Route path="/resources/master" element={<ProtectedRoute moduleKey="resources"><ResourceMaster /></ProtectedRoute>} />
          <Route path="/resource/:id" element={<ProtectedRoute moduleKey="resources"><ResourceDetail /></ProtectedRoute>} />
          <Route path="/admin/calendar" element={<ProtectedRoute><CalendarSettings /></ProtectedRoute>} />
          <Route path="/admin/panel" element={<AdminRoute><AdminPanel /></AdminRoute>} />
          <Route path="/admin/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
          <Route path="/admin/email-settings" element={<AdminRoute><EmailSettings /></AdminRoute>} />
          <Route path="/admin/sap-integration" element={<ProtectedRoute><SapB1Settings /></ProtectedRoute>} />
          <Route path="/admin/sap-live-updates" element={<ProtectedRoute><SapWebhookMonitor /></ProtectedRoute>} />
          <Route path="/sap-update-check" element={<ProtectedRoute><SapUpdateCheck /></ProtectedRoute>} />
          <Route path="/admin/numbering" element={<AdminRoute><NumberingSettings /></AdminRoute>} />
          <Route path="/financials/aging/customer-receivables" element={<ProtectedRoute moduleKey="financials"><CustomerReceivablesAging /></ProtectedRoute>} />
          <Route path="/financials/aging/vendor-liabilities" element={<ProtectedRoute moduleKey="financials"><VendorLiabilitiesAging /></ProtectedRoute>} />
          <Route path="/vendor-management" element={<ProtectedRoute anyModule={['purchasing', 'financials']}><VendorManagement /></ProtectedRoute>} />
          <Route path="/vendor-management/performance" element={<ProtectedRoute anyModule={['purchasing', 'financials']}><VendorPerformance /></ProtectedRoute>} />
          <Route path="/vendor-management/score" element={<ProtectedRoute anyModule={['purchasing', 'financials']}><VendorScore /></ProtectedRoute>} />
          <Route path="/vendor-management/risk" element={<ProtectedRoute anyModule={['purchasing', 'financials']}><VendorRiskManagement /></ProtectedRoute>} />
          <Route path="/vendor-management/risk-list" element={<VendorRiskList />}/>
          <Route path="/vendor-management/vendor/:id" element={<ProtectedRoute anyModule={['purchasing', 'financials']}><VendorMgmtDetail /></ProtectedRoute>} />
          <Route path="/vendor-management/risk/:vendorName" element={<ProtectedRoute anyModule={['purchasing', 'financials']}><VendorRiskDetail /></ProtectedRoute>}/>
          <Route path="/vendor-management/delivery-performance"element={<VendorDeliveryPerformance />}/>
          <Route path="/vendor-management/delivery-performance/list"element={<DeliveryVendorList />}/>
          <Route path="/vendor-management/quality-list"element={<QualityVendorList />}/>
          <Route path="/vendor-management/procurement-list"element={<ProcurementVendorList />}/>
          <Route path="/vendor-management/financial-list"element={<FinancialVendorList />}/>
          <Route path="/vendor-management/activity"element={<VendorActivity />}/>
          <Route path="/financials/customer-bp" element={<ProtectedRoute moduleKey="financials"><CustomerBusinessPartnerOverview /></ProtectedRoute>} />
          <Route path="/projects/total" element={<AdminRoute><TotalProjects /></AdminRoute>} />
          <Route path="/guide" element={<ProtectedRoute><UserGuide /></ProtectedRoute>} />
          <Route path="/access-denied" element={<AccessDenied />} />
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Layout>
    </>
  );
}
